import React, { useState } from "react";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import Button from "./Button";
import TutorialTip from "./TutorialTip";

/**
 * SlidingContainer - A reusable component for creating sliding interactions
 *
 * Usage Example:
 *
 * <SlidingContainer
 *   title="My Sliding Item"
 *   hasAction={true}
 *   actionLabel="View Details"
 *   onItemClick={() => {}}
 *   onActionClick={() => {}}
 * />
 */

interface SlidingContainerProps {
  title: React.ReactNode;
  hasAction?: boolean;
  actionLabel?: React.ReactNode;
  onActionClick?: () => void;
  className?: string;
  actionButtonClassName?: string;
  indicatorClassName?: string;
  slideDistance?: number; // Distance to slide in pixels
  actionButtonWidth?: number; // Width of the action button area
  indicatorWidth?: number; // Width of the indicator bar
  showTutorial?: boolean; // Whether to show tutorial tip
  tutorialContent?: React.ReactNode; // Custom tutorial content
}

export default function SlidingContainer({
  title,
  hasAction = false,
  actionLabel = "View Details",
  onActionClick,
  className = "",
  actionButtonClassName = "",
  indicatorClassName = "",
  slideDistance = 60,
  showTutorial = false,
  tutorialContent,
}: SlidingContainerProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const handleItemClick = () => {
    if (hasAction) {
      setIsExpanded(!isExpanded);
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50; // Minimum swipe distance

    if (isLeftSwipe && hasAction) {
      handleItemClick();
    }

    setTouchStart(null);
    setTouchEnd(null);
  };

  const handleActionClick = () => {
    onActionClick?.();
  };

  return (
    <TutorialTip
      direction="top"
      tutorialId="sliding-container-tutorial"
      maxWidth={250}
      content={
        tutorialContent || (
          <div className="text-sm">
            <p className="font-medium mb-2">View Your Responses</p>
            <p>
              Click the arrow{" "}
              <span className="inline-flex items-center mx-1">
                <ChevronLeftIcon className="w-3 h-3 text-electrum" />
              </span>{" "}
              or swipe left to reveal your saved responses.
            </p>
          </div>
        )
      }
      isVisible={showTutorial && hasAction}
    >
      <div
        className={`relative overflow-hidden bg-gray-50 hover:bg-gray-100 transition-all duration-300 ease-in-out ${className}`}
      >
        <div
          className="p-2 pr-5 cursor-pointer transition-all duration-300 ease-in-out"
          style={{
            transform:
              isExpanded && hasAction
                ? `translateX(-${slideDistance}px)`
                : "translateX(0)",
          }}
          onClick={handleItemClick}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {title}
        </div>

        {/* Indicator bar for items with actions */}
        {hasAction && (
          <div
            className={`absolute right-0 top-0 h-full bg-electrum flex items-center justify-center cursor-pointer rounded-l-lg ${indicatorClassName}`}
            onClick={handleItemClick}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <ChevronLeftIcon className="w-3 h-3 text-white" />
          </div>
        )}

        {/* Revealed action button - only show if there's an action */}
        {hasAction && (
          <div
            className={`absolute right-0 top-0 h-full bg-electrum hover:bg-electrum-300 flex items-center justify-center transition-all duration-300 ease-in-out rounded-l-lg ${actionButtonClassName}`}
            style={{
              transform: isExpanded ? "translateX(0)" : "translateX(100%)",
            }}
          >
            <Button
              size="sm"
              variant="alternative"
              className="flex items-center justify-center gap-2 text-white border-white/20 !px-1 !py-1 w-full h-full"
              onClick={handleActionClick}
            >
              <span className="text-xs font-medium text-white">
                {actionLabel}
              </span>
            </Button>
          </div>
        )}
      </div>
    </TutorialTip>
  );
}
